/**
 * THE FIRST-OPEN GATE (launch-marketing-pages design M11; th-0v4ebt.6).
 *
 * A newcomer's first open has one order: intro → dock → beacon arms → held
 * toasts and banners release, once Artemis's welcome card has rendered. Noise
 * waits behind this ONE bounded hold, never behind a per-banner timer. It lives
 * in a leaf that imports only `vue`, because `stores/notifications.js` (a boot
 * store) reads it.
 *
 *   - Readers: `stores/notifications.js` queues every toast push while
 *     `holding`, and `WorkspaceShell` renders its banner slot only while not.
 *   - Feeders: `WorkspaceShell` calls `open()` when it starts the intro (its
 *     `introPending` read true at setup, or turning true on a mounted shell),
 *     `docked()` at the intro's dock and `abandoned()` when the intro unmounts
 *     before its dock; the welcome card calls `cardRendered()` on mount.
 *
 * It releases on the EARLIEST of:
 *   1. the dock has run AND the card has rendered (a card rendered before the
 *      dock counts at the dock);
 *   2. `HOLD_AFTER_DOCK_MS` after the dock;
 *   3. the intro abandoned (a resize crossing, Back, an error).
 *
 * `open()` starts a hold for an intro that is starting, so a second intro (the
 * next desktop mount replaying one abandoned after its dock) restarts the
 * cycle: the earlier dock's timer can no longer release it. Every other call
 * outside a hold is a no-op, and a release happens once.
 */
import { readonly, ref } from "vue";

export const HOLD_AFTER_DOCK_MS = 8000;

const holdingState = ref(false);
let hasDocked = false;
let hasCardRendered = false;
let dockTimer = null;

export const holding = readonly(holdingState);

function clearDockTimer() {
  if (dockTimer != null) clearTimeout(dockTimer);
  dockTimer = null;
}

function release() {
  if (!holdingState.value) return;
  clearDockTimer();
  holdingState.value = false;
}

export function open() {
  clearDockTimer();
  hasDocked = false;
  hasCardRendered = false;
  holdingState.value = true;
}

export function docked() {
  if (!holdingState.value || hasDocked) return;
  hasDocked = true;
  if (hasCardRendered) {
    release();
    return;
  }
  dockTimer = setTimeout(release, HOLD_AFTER_DOCK_MS);
}

export function cardRendered() {
  if (!holdingState.value) return;
  hasCardRendered = true;
  if (hasDocked) release();
}

export function abandoned() {
  release();
}

export function __resetFirstOpenGateForTests() {
  clearDockTimer();
  hasDocked = false;
  hasCardRendered = false;
  holdingState.value = false;
}
